import { Clock, Loader2, RotateCw, Shield, Trash2, UserPlus, Users, UserX } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '../../lib/trpc';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Checkbox } from '../../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Skeleton } from '../../ui/skeleton';
import { UserAvatar } from '../../ui/user-avatar';
import { ProductSeatToggle } from '../billing/ProductSeatAssignment';
import type { ProductKey } from '../billing/SubscriptionBillingPanel';

interface WorkspaceMembersSectionProps {
  productKey: ProductKey;
  productLabel: string;
  isAdmin: boolean;
  currentUserId: number | undefined;
}

/**
 * The invitation exists whether or not the email left the building. When delivery fails the
 * admin gets the link instead of an error, because the token is the invitation and the
 * alternative — losing it to a mail outage — is strictly worse.
 */
function announceInviteDelivery(
  invite: { url: string; emailDelivered: boolean } | null,
  deliveredMessage: string
) {
  if (!invite) return;
  if (invite.emailDelivered) {
    toast.success(deliveredMessage);
    return;
  }
  toast.warning('Invitation created, but the email could not be sent. Share the link yourself.', {
    duration: 10000,
    action: {
      label: 'Copy link',
      onClick: () => {
        void navigator.clipboard?.writeText(invite.url);
        toast.success('Invite link copied.');
      },
    },
  });
}

/**
 * Members and pending invitations are two cards rather than one, so the roster
 * doesn't push the invite state off the fold on a workspace with many members.
 */
export function WorkspaceMembersSection({
  productKey,
  productLabel,
  isAdmin,
  currentUserId,
}: WorkspaceMembersSectionProps) {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'editor' | 'viewer'>('editor');
  const [grantProductAccess, setGrantProductAccess] = useState(true);
  const utils = trpc.useUtils();

  const { data: members, isLoading: isMembersLoading } = trpc.workspace.listMembers.useQuery();

  const { data: pendingInvites, isLoading: isInvitesLoading } =
    trpc.workspace.listPendingInvitations.useQuery(undefined, { enabled: isAdmin });
  const { data: suggestedTeammates } = trpc.onboarding.suggestedTeammates.useQuery(undefined, {
    enabled: isAdmin,
  });

  const { data: productSeats } = trpc.workspace.listProductSeats.useQuery(
    { productKey },
    { enabled: isAdmin }
  );
  const pausedSeatUserIds = new Set(
    productSeats?.filter((seat) => seat.seatStatus === 'paused').map((seat) => seat.userId)
  );
  const activeSeatUserIds = new Set(
    productSeats?.filter((seat) => seat.seatStatus === 'active').map((seat) => seat.userId)
  );

  const addMemberMutation = trpc.workspace.addMember.useMutation({
    onSuccess: (data) => {
      if (data?.directAdded) {
        toast.success('Member added directly to workspace!');
      } else {
        announceInviteDelivery(data?.invite ?? null, 'Invitation email sent successfully!');
      }
      if (data?.seatAssignmentIssues.length) {
        toast.warning('Member added, but selected product access needs your attention.');
      }
      setInviteEmail('');
      setInviteRole('editor');
      setGrantProductAccess(true);
      utils.workspace.listMembers.invalidate();
      utils.workspace.listPendingInvitations.invalidate();
      utils.workspace.getBillingState.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to add member.');
    },
  });

  const updateRoleMutation = trpc.workspace.updateMemberRole.useMutation({
    onSuccess: () => {
      toast.success('Member role updated successfully!');
      utils.workspace.listMembers.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update member role.');
    },
  });

  const removeMemberMutation = trpc.workspace.removeMember.useMutation({
    onSuccess: () => {
      toast.success('Member removed successfully!');
      utils.workspace.listMembers.invalidate();
      utils.workspace.getBillingState.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to remove member.');
    },
  });

  const resendInviteMutation = trpc.workspace.resendInvitation.useMutation({
    onSuccess: (data) => {
      announceInviteDelivery(
        data?.invite ?? null,
        'Invitation email resent. The previous link no longer works.'
      );
      utils.workspace.listPendingInvitations.invalidate();
    },
    onError: (error) => toast.error(error.message || 'Could not resend invitation.'),
  });

  const cancelInviteMutation = trpc.workspace.cancelInvitation.useMutation({
    onSuccess: () => {
      toast.success('Invitation revoked successfully!');
      utils.workspace.listPendingInvitations.invalidate();
      utils.workspace.getBillingState.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to revoke invitation.');
    },
  });

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    addMemberMutation.mutate({
      email: inviteEmail.trim(),
      role: inviteRole,
      assignProductKeys: grantProductAccess ? [productKey] : [],
      portal: productKey === 'anchor_contact' ? 'skill-viper' : 'clinic',
    });
  };

  return (
    <div className="space-y-8">
      <Card className="border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
        <CardHeader className="border-b border-border/50 bg-muted/30">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <CardTitle>Workspace Team Members</CardTitle>
          </div>
          <CardDescription>
            Manage who has access to this workspace and control their editing permissions.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {isAdmin ? (
            <>
              <form
                onSubmit={handleInvite}
                className="max-w-lg space-y-3 border-b border-border/50 pb-6"
              >
                <div className="flex gap-3 items-end">
                  <div className="flex-1 space-y-1">
                    <label className="text-xs font-semibold text-muted-foreground [text-transform:var(--eyebrow-transform,uppercase)] [letter-spacing:var(--eyebrow-tracking,0.05em)]">
                      Invite teammate
                    </label>
                    <input
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="colleague@company.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="w-full rounded-lg border border-border bg-background/50 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary placeholder:text-muted-foreground/60"
                    />
                  </div>
                  <div className="w-28 space-y-1">
                    <label className="text-xs font-semibold text-muted-foreground [text-transform:var(--eyebrow-transform,uppercase)] [letter-spacing:var(--eyebrow-tracking,0.05em)]">
                      Role
                    </label>
                    <Select
                      value={inviteRole}
                      onValueChange={(value) => setInviteRole(value as typeof inviteRole)}
                    >
                      <SelectTrigger className="h-9 rounded-lg text-xs bg-background/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="editor">Editor</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="submit"
                    disabled={addMemberMutation.isPending}
                    className="rounded-lg h-9 px-4 shrink-0 transition-transform active:scale-95"
                  >
                    {addMemberMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <UserPlus className="h-4 w-4 mr-2" />
                    )}
                    Invite
                  </Button>
                </div>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <Checkbox
                    checked={grantProductAccess}
                    onCheckedChange={(checked) => setGrantProductAccess(checked === true)}
                  />
                  Give access to {productLabel} when they join
                </label>
              </form>
              {suggestedTeammates && suggestedTeammates.length > 0 && (
                <div className="max-w-lg rounded-lg border border-border/50 bg-muted/20 p-3 text-xs">
                  <p className="font-medium text-foreground">
                    People you regularly email at your company
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {suggestedTeammates.map((teammate) => (
                      <Button
                        key={teammate.email}
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="h-7"
                        onClick={() => setInviteEmail(teammate.email)}
                      >
                        {teammate.email}
                      </Button>
                    ))}
                  </div>
                  <p className="mt-2 text-muted-foreground">
                    Same company domain only. Choosing one fills the invite form; nothing sends
                    automatically.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-3 p-3 rounded-lg border border-primary/20 bg-primary/5 text-xs text-muted-foreground max-w-lg">
              <Shield className="h-4 w-4 text-primary shrink-0" />
              <span>
                Only workspace <strong>Admins</strong> can invite new team members or modify roles.
              </span>
            </div>
          )}

          <div className="space-y-4 pt-2">
            <h3 className="text-xs font-semibold text-muted-foreground [text-transform:var(--eyebrow-transform,uppercase)] [letter-spacing:var(--eyebrow-tracking,0.05em)]">
              Active Members
            </h3>

            {isMembersLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-14 w-full rounded-xl" />
                <Skeleton className="h-14 w-full rounded-xl" />
              </div>
            ) : members && members.length > 0 ? (
              <div className="grid gap-3">
                {members.map((member) => {
                  const isSelf = member.id === currentUserId;
                  return (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 rounded-xl border border-border/50 bg-background/30 hover:bg-background/50 transition-all"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <UserAvatar user={member} size="md" />
                        <div className="min-w-0">
                          <div className="font-semibold text-sm flex items-center gap-2">
                            <span className="truncate">{member.fullName || member.email}</span>
                            {isSelf && (
                              <Badge
                                variant="outline"
                                className="text-[9px] [text-transform:var(--eyebrow-transform,uppercase)] [letter-spacing:var(--eyebrow-tracking,0.05em)] font-bold py-0 px-1 border-primary/20 text-primary bg-primary/5"
                              >
                                You
                              </Badge>
                            )}
                          </div>
                          {member.fullName && (
                            <div className="text-xs text-muted-foreground truncate">
                              {member.email}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        {/* Per-product seat toggle — available on every tier, including Free,
                            because the Free plan ships with real assignable seats. */}
                        {isAdmin && (
                          <ProductSeatToggle
                            userId={member.id}
                            seatStatus={
                              activeSeatUserIds.has(member.id)
                                ? 'active'
                                : pausedSeatUserIds.has(member.id)
                                  ? 'paused'
                                  : 'none'
                            }
                            productKey={productKey}
                            productLabel={productLabel}
                          />
                        )}

                        {/* Role selection dropdown */}
                        {isAdmin && !isSelf ? (
                          <Select
                            value={member.role || 'viewer'}
                            onValueChange={(val) => {
                              updateRoleMutation.mutate({
                                userId: member.id,
                                role: val as 'admin' | 'editor' | 'viewer',
                              });
                            }}
                          >
                            <SelectTrigger className="w-28 h-8 rounded-lg text-xs bg-background/50">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="editor">Editor</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="secondary" className="capitalize text-xs py-0.5 px-2">
                            {member.role || 'viewer'}
                          </Badge>
                        )}

                        {/* Remove member button */}
                        {isAdmin && !isSelf && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Are you sure you want to remove ${member.fullName || member.email} from the workspace?`
                                )
                              ) {
                                removeMemberMutation.mutate({ userId: member.id });
                              }
                            }}
                            disabled={removeMemberMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-sm text-muted-foreground">
                No active members found.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {isAdmin && (
        <Card className="border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
          <CardHeader className="border-b border-border/50 bg-muted/30">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              <CardTitle>Pending Invitations</CardTitle>
            </div>
            <CardDescription>
              Invitations waiting for acceptance. Seats are assigned only when the recipient joins.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {isInvitesLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-14 w-full rounded-xl" />
              </div>
            ) : pendingInvites && pendingInvites.length > 0 ? (
              <div className="grid gap-3">
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between p-3 rounded-xl border border-dashed border-border/60 bg-background/10 hover:bg-background/20 transition-all"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-full bg-muted/40 text-muted-foreground flex items-center justify-center font-bold text-xs shrink-0 border border-border/40">
                        @
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{invite.email}</div>
                        <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                          <span>Invited by {invite.invitedBy}</span>
                          <span>•</span>
                          <span>
                            Expires {new Date(invite.expiresAt * 1000).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <Badge
                        variant="outline"
                        className="capitalize text-xs text-primary border-primary/20 bg-primary/5 py-0.5 px-2"
                      >
                        Pending {invite.role || 'viewer'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg"
                        title="Resend invitation"
                        onClick={() => resendInviteMutation.mutate({ inviteId: invite.id })}
                        disabled={resendInviteMutation.isPending}
                      >
                        {resendInviteMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RotateCw className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg"
                        title="Revoke Invitation"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Are you sure you want to revoke the invitation for ${invite.email}?`
                            )
                          ) {
                            cancelInviteMutation.mutate({ inviteId: invite.id });
                          }
                        }}
                        disabled={cancelInviteMutation.isPending}
                      >
                        <UserX className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground/60 italic">No pending invitations.</div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
